import type { ReactElement } from 'react';
import React from 'react';
import { flexRender } from '@tanstack/react-table';
import type { SortingState, Table } from '@tanstack/react-table';
import clsx from 'clsx';
import Loading from 'atom-components/loading';
import TableLite from 'atom-components/table-lite/table';
import NoResultsAlert, { type NoResultsAlertProps } from './no-results-alert';
import SortColumnButton from './sort-column-button';
import styles from './tanstack-table.module.scss';

type Props<TData> = {
  isLoading?: boolean;
  tableInstance: Table<TData>;
  printable?: boolean;
} & NoResultsAlertProps;

/**
 * Accepts a TanStack Table 8+ table instance as a prop and returns an HTML table (or an alert if
 * there are zero rows of data).
 */
export default function TanStackTable<TData extends object>({
  isLoading,
  tableInstance,
  printable,
  entity,
  hideSuggestion
}: Props<TData>): ReactElement {
  if (!isLoading && tableInstance.getRowModel().rows.length === 0) {
    return <NoResultsAlert entity={entity} hideSuggestion={hideSuggestion} />;
  }

  return (
    <TableLite className="table-hover">
      <TableLite.Head printable={printable}>
        {tableInstance.getHeaderGroups().map((headerGroup) => (
          <TableLite.Row key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableLite.Header
                key={header.id}
                size={header.getSize()}
                className={clsx({
                  [`${styles.actionsHeader}`]: header.id === 'actions'
                })}
              >
                <SortColumnButton
                  header={header}
                  sorting={tableInstance.getState().sorting}
                  onSort={(updatedSorting: SortingState): void =>
                    tableInstance.setSorting(updatedSorting)
                  }
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </SortColumnButton>
              </TableLite.Header>
            ))}
          </TableLite.Row>
        ))}
      </TableLite.Head>
      <TableLite.Body>
        {isLoading && (
          <TableLite.Row>
            <TableLite.DataCell colSpan={tableInstance.getAllColumns().length}>
              <div className="container text-center m-10">
                <Loading overlay={false} positionAbsolute={false} />
              </div>
            </TableLite.DataCell>
          </TableLite.Row>
        )}
        {!isLoading &&
          tableInstance.getRowModel().rows.map((row) => (
            <TableLite.Row
              key={row.id}
              className={
                tableInstance.getState().rowSelection && row.getIsSelected() ? 'table-active' : ''
              }
            >
              {row.getVisibleCells().map((cell) => (
                <TableLite.DataCell
                  key={cell.id}
                  className={clsx({
                    [`${styles.actionsCell}`]: cell.column.id === 'actions'
                  })}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableLite.DataCell>
              ))}
            </TableLite.Row>
          ))}
      </TableLite.Body>
    </TableLite>
  );
}
